import { notify } from "@/common/components/common/Notification";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { axiosConfig } from "./axiosConfig";

export interface ApiError {
  message: string;
  status: number;
  errors?: any;
}

export interface RequestConfig extends AxiosRequestConfig {
  showNotification?: boolean;
  customErrorMessage?: string;
}

const pendingRequests = new Map<string, Promise<any>>();

export const get = async <T = any>(
  url: string,
  config?: RequestConfig
): Promise<T> => {
  // Create a unique key for deduplication based on URL and params
  const requestKey = `GET:${url}:${JSON.stringify(config?.params ?? {})}`;

  // If a request with the same URL and params is already in progress, return its promise
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  const fetchPromise = (async () => {
    try {
      const response: AxiosResponse<T> = await axiosConfig.get(url, config);
      return response.data;
    } catch (error: any) {
      handelError(error);
      throw error;
    } finally {
      // Clear the request from pending once it's finished
      pendingRequests.delete(requestKey);
    }
  })();

  pendingRequests.set(requestKey, fetchPromise);
  return fetchPromise;
};

export const post = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<T> => {
  const requestKey = `POST:${url}:${JSON.stringify(data ?? {})}:${JSON.stringify(config?.params ?? {})}`;

  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  const fetchPromise = (async () => {
    try {
      const response: AxiosResponse<T> = await axiosConfig.post(
        url,
        data,
        config
      );
      return response.data;
    } catch (error: any) {
      handelError(error);
      throw error;
    } finally {
      pendingRequests.delete(requestKey);
    }
  })();

  pendingRequests.set(requestKey, fetchPromise);
  return fetchPromise;
};

export const put = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<T> => {
  const requestKey = `PUT:${url}:${JSON.stringify(data ?? {})}:${JSON.stringify(config?.params ?? {})}`;

  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  const fetchPromise = (async () => {
    try {
      const response: AxiosResponse<T> = await axiosConfig.put(
        url,
        data,
        config
      );
      return response.data;
    } catch (error: any) {
      handelError(error);
      throw error;
    } finally {
      pendingRequests.delete(requestKey);
    }
  })();

  pendingRequests.set(requestKey, fetchPromise);
  return fetchPromise;
};

export const patch = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<T> => {
  const requestKey = `PATCH:${url}:${JSON.stringify(data ?? {})}:${JSON.stringify(config?.params ?? {})}`;

  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  const fetchPromise = (async () => {
    try {
      const response: AxiosResponse<T> = await axiosConfig.patch(
        url,
        data,
        config
      );
      return response.data;
    } catch (error: any) {
      handelError(error);
      throw error;
    } finally {
      pendingRequests.delete(requestKey);
    }
  })();

  pendingRequests.set(requestKey, fetchPromise);
  return fetchPromise;
};

export const del = async <T = any>(
  url: string,
  config?: RequestConfig
): Promise<T> => {
  const requestKey = `DELETE:${url}:${JSON.stringify(config?.params ?? {})}`;

  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  const fetchPromise = (async () => {
    try {
      const response: AxiosResponse<T> = await axiosConfig.delete(url, config);
      return response.data;
    } catch (error: any) {
      handelError(error);
      throw error;
    } finally {
      pendingRequests.delete(requestKey);
    }
  })();

  pendingRequests.set(requestKey, fetchPromise);
  return fetchPromise;
};

const handelError = (error: any) => {
  if (
    error.code === "ERR_NETWORK" ||
    error.message?.includes("Network Error")
  ) {
    notify.error(
      "Network Error",
      "Unable to connect to the server. Please check your internet connection."
    );
  }

  // const { status, data } = error.response;
  // const errorMessage =
  //   data?.error?.message ??
  //   data?.message ??
  //   data?.error ??
  //   data?.non_field_errors ??
  //   "An error occurred";
  //   const errorCode = data?.error?.code ?? status;
  //   notify.error(errorCode, errorMessage);
};
